"use client";
import { type RefObject, useEffect } from "react";
import type { Noop } from "@/types";

/**
 * Hook for detecting clicks outside of the specified element
 * @param ref Reference to the element to check clicks against
 * @param callback Function to call when a click outside occurs
 * @param isActive Boolean to determine when the listener should be active
 */
export function useClickOutside<T extends HTMLElement = HTMLElement>(
  ref: RefObject<T | null>,
  callback: Noop,
  isActive = true,
) {
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        callback();
      }
    }

    if (isActive) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref, callback, isActive]);
}
